import React, { useCallback, useEffect } from 'react';
import { useMeasure } from 'react-use';

import { cn } from '@lyearn/style';
import { Box, Stack } from '@lyearn/ui';
import { useRouteChrome } from '@/components/RouteChrome';
import { useSidebarCollapse } from '@/components/SidebarCollapse';
import { useAppConfig } from '@/hooks/useAppConfig';
import useNavigation from '@/hooks/useNavigation';
import useScrollProgress from '@/hooks/useScrollProgress';
import routes from '@/pages/corporate/routes';

import HeaderBreadcrumbs from '../components/HeaderBreadcrumbs';
import SearchBar from '../components/SearchBar';
import SearchTrigger from '../components/SearchTrigger';
import SidebarToggle from '../components/SidebarToggle';
import UnifiedRightNode from '../components/UnifiedRightNode';
import { DefaultHeaderProps } from '../types';
import DefaultHeaderPreUnified from './index.desktop.preUnified';
import { headerOnBg } from './style';

const DefaultHeader = React.memo(
  (
    props: DefaultHeaderProps & {
      animate: boolean;
    },
  ) => {
    const {
      LeftNode,
      RightNode,
      CenterNode,
      className,
      showNavigationTabs,
      SubHeader,
      animate,
      isDisabled,
    } = props;

    const [headerRef, { height: computedHeight }] = useMeasure<HTMLDivElement>();
    const chrome = useRouteChrome();
    const { isCollapsed: isSidebarCollapsed, toggle: toggleSidebar } = useSidebarCollapse();
    const navigation = useNavigation();

    useEffect(() => {
      document.body.style.setProperty('--ly-header-height', `${computedHeight}px`);
      return () => {
        document.body.style.removeProperty('--ly-header-height');
      };
    }, [computedHeight]);

    const openSearch = useCallback(() => {
      navigation.push(routes.Search());
    }, [navigation]);

    const closeSearch = useCallback(() => {
      navigation.push(routes.Home(), { query: {}, appendQuery: false });
    }, [navigation]);

    const HeaderLeftNode = showNavigationTabs ? null : LeftNode;

    // On the search results page itself, show the real editable search box instead of
    // the inert trigger button — otherwise typing there does nothing.
    const isOnSearchPage = navigation.pathname === routes.Search();
    const SearchNode = !chrome.search ? null : isOnSearchPage ? (
      <SearchBar onClose={closeSearch} />
    ) : (
      <SearchTrigger onOpen={openSearch} />
    );
    const HeaderCenterNode = !showNavigationTabs && CenterNode ? CenterNode : SearchNode;

    const HeaderRightNode =
      showNavigationTabs || RightNode === undefined ? <UnifiedRightNode /> : RightNode;

    const SubHeaderNode = showNavigationTabs || SubHeader === undefined ? null : SubHeader;

    return (
      <Stack
        ref={headerRef}
        className={cn(
          'sticky top-0 z-50',
          animate ? 'animate' : '',
          headerOnBg(showNavigationTabs),
          isDisabled ? 'pointer-events-none' : '',
        )}
        component="header"
        direction="col">
        <Box
          className={cn(
            'relative flex h-50 flex-shrink-0 items-center justify-between border-b border-border bg-bg-primary text-text-primary pe-8',
            className,
          )}>
          <Stack
            alignItems="center"
            className={cn('min-w-0', chrome.sidebar ? '' : 'ps-20')}
            gap={12}>
            {chrome.sidebar ? (
              // A 10px gutter either side of the tile; the bar's own height centres it.
              <Box className="flex h-50 flex-shrink-0 items-center px-10">
                <SidebarToggle isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} />
              </Box>
            ) : null}
            <HeaderBreadcrumbs />
            {HeaderLeftNode}
          </Stack>
          {HeaderCenterNode ? (
            <Stack className="min-w-0 flex-1" justifyContent="center">
              {HeaderCenterNode}
            </Stack>
          ) : null}
          <Stack alignItems="center" className="flex-shrink-0" gap={12}>
            {HeaderRightNode}
          </Stack>
        </Box>
        {SubHeaderNode}
      </Stack>
    );
  },
);

function DefaultHeaderWrapper(props: DefaultHeaderProps) {
  const { scrollProgress } = useScrollProgress(props.scrollContainerEl);
  const animate = !!scrollProgress;
  const {
    ui: { isUnifiedSidebar },
  } = useAppConfig();

  if (isUnifiedSidebar) {
    return <DefaultHeader animate={animate} {...props} />;
  }

  // Everything the header did before the unified shell, feature-flag branching included:
  // the nav-tab bar under EnableUnifiedDashboard, the older bar without it.
  return <DefaultHeaderPreUnified {...props} />;
}

export default DefaultHeaderWrapper;
